import { useState } from "react";
import { Download, Share2, Check } from "lucide-react";
import { FaFacebookF, FaWhatsapp, FaLinkedinIn, FaInstagram } from "react-icons/fa";

/* Shareable assessment card block: card preview, download, native card share
   (image file via the OS share sheet, best for Instagram/WhatsApp on mobile)
   and direct network buttons. Used on Level 0 and Level 1 results. */
const ShareAssessment = ({ cardUrl, downloadUrl, shareText, shareUrl, variant = "full", testId = "share-assessment" }) => {
  const [note, setNote] = useState("");
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedText = encodeURIComponent(shareText);

  const flash = (msg) => {
    setNote(msg);
    setTimeout(() => setNote(""), 4000);
  };

  /* native share with the actual card image; returns false when unsupported */
  const shareCardFile = async () => {
    try {
      const blob = await (await fetch(cardUrl)).blob();
      const file = new File([blob], "lifespan-card.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text: shareText, title: "The Lifespan Project" });
        return true;
      }
    } catch { /* cancelled or unsupported */ }
    return false;
  };

  const onShareCard = async () => {
    const ok = await shareCardFile();
    if (!ok) flash("This browser can't share images directly. Download the card and attach it instead.");
  };

  const onInstagram = async () => {
    const ok = await shareCardFile();
    if (!ok) {
      window.open(downloadUrl, "_blank");
      flash("Card downloaded: attach it to your Instagram post or story.");
    }
  };

  const networks = [
    { id: "whatsapp", icon: FaWhatsapp, href: `https://wa.me/?text=${encodedText}%0A${encodedUrl}`, label: "Share on WhatsApp" },
    { id: "linkedin", icon: FaLinkedinIn, href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`, label: "Share on LinkedIn" },
    { id: "facebook", icon: FaFacebookF, href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`, label: "Share on Facebook" },
  ];

  return (
    <div data-testid={testId}>
      {variant === "full" && (
        <img src={cardUrl} alt="Your shareable assessment card" data-testid={`${testId}-preview`}
          className="mx-auto w-full max-w-lg rounded-2xl border border-white/10 shadow-2xl" loading="lazy" />
      )}
      <div className={`flex flex-wrap items-center justify-center gap-2.5 ${variant === "full" ? "mt-5" : ""}`}>
        <a href={downloadUrl} data-testid={`${testId}-download`}
          className="bg-gradient-green inline-flex min-h-[44px] items-center gap-2 rounded-full px-5 text-sm font-bold text-black">
          <Download className="h-4 w-4" /> Download Card
        </a>
        <button type="button" onClick={onShareCard} data-testid={`${testId}-native`}
          className="bg-gradient-accent inline-flex min-h-[44px] items-center gap-2 rounded-full px-5 text-sm font-semibold text-white">
          <Share2 className="h-4 w-4" /> Share Card
        </button>
      </div>
      <div className="mt-3 flex items-center justify-center gap-2.5">
        {networks.map((n) => (
          <a key={n.id} href={n.href} target="_blank" rel="noreferrer" aria-label={n.label} data-testid={`${testId}-${n.id}`}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-fog transition-colors duration-300 hover:border-cyan-300/50 hover:text-white">
            <n.icon className="h-4 w-4" />
          </a>
        ))}
        <button type="button" onClick={onInstagram} aria-label="Share on Instagram" data-testid={`${testId}-instagram`}
          className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-fog transition-colors duration-300 hover:border-cyan-300/50 hover:text-white">
          <FaInstagram className="h-4 w-4" />
        </button>
      </div>
      {note && (
        <p className="mt-3 flex items-center justify-center gap-1.5 text-center text-xs text-green-300" data-testid={`${testId}-note`}>
          <Check className="h-3.5 w-3.5" /> {note}
        </p>
      )}
      {variant === "full" && (
        <p className="mt-3 text-center text-[11px] text-white/35">
          Inspire others: recommend they take this free assessment for a healthier, longer life.
        </p>
      )}
    </div>
  );
};

export default ShareAssessment;
